/**
 * Sending rules.
 *
 * These caps used to live only in the database, which meant "why did it say
 * Daily cap reached" was unanswerable from the product and unchangeable without
 * an API call. They are operator policy, so the operator edits them here.
 *
 * Every cap accepts 0, meaning no limit. That is deliberate: an operator who
 * wants to send freely should be able to say so plainly rather than type 99999
 * and hope. The consequence is stated next to the field rather than hidden
 * behind a confirm dialog.
 *
 * One rule is NOT here, on purpose: a customer's per-category opt-out. That is
 * their choice, not a throttle, and nothing on this screen overrides it.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import { Card, Field, PageHeader, PillButton, TextInput } from '../../components/shared/primitives'
import { notificationsApi } from '../../features/notifications/api'
import type { EngineSettings } from '../../features/notifications/types'
import '../../styles/notifications.css'

type FieldSpec = {
  key: string
  label: string
  unit: string
  hint: string
  /** Whether 0 is meaningful for this field. Quiet hours are clock hours, not caps. */
  unlimited?: boolean
}

const CAPS: FieldSpec[] = [
  {
    key: 'notif.max_marketing_per_day',
    label: 'Marketing notifications per customer, per day',
    unit: 'per day',
    hint: 'How many campaigns one customer can receive in 24 hours.',
    unlimited: true,
  },
  {
    key: 'notif.max_marketing_per_week',
    label: 'Marketing notifications per customer, per week',
    unit: 'per week',
    hint: 'The rolling seven-day ceiling.',
    unlimited: true,
  },
  {
    key: 'notif.min_gap_minutes',
    label: 'Minimum gap between marketing notifications',
    unit: 'minutes',
    hint: 'Stops two campaigns landing back to back on the same phone.',
    unlimited: true,
  },
  {
    key: 'notif.transactional_window_minutes',
    label: 'Hold marketing after an order update',
    unit: 'minutes',
    hint: 'Marketing waits this long after a customer receives an order notification, so a campaign never lands on top of a delivery alert. Held, not dropped.',
    unlimited: true,
  },
]

const QUIET: FieldSpec[] = [
  { key: 'notif.quiet_start', label: 'Quiet hours start', unit: 'hour (IST)', hint: '0 to 23. Campaigns due inside the window are held until it opens, never dropped.' },
  { key: 'notif.quiet_end', label: 'Quiet hours end', unit: 'hour (IST)', hint: 'Set both to the same value to disable quiet hours.' },
]

export default function NotificationSettingsPage() {
  const colors = useColors()
  const navigate = useNavigate()

  const [settings, setSettings] = useState<EngineSettings | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    try {
      const data = await notificationsApi.getSettings()
      setSettings(data)
      setDraft(data.settings)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the sending rules')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async () => {
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const payload = Object.fromEntries(
        Object.entries(draft).filter(([key]) => key.startsWith('notif.'))
      )
      await notificationsApi.updateSettings(payload)
      await load()
      setNotice('Saved. New rules apply to the next campaign; anything already sending is unaffected.')
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string } } }).response
      setError(response?.data?.message || 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  const renderField = (spec: FieldSpec) => {
    const value = draft[spec.key] ?? ''
    const isUnlimited = spec.unlimited && Number(value) <= 0
    return (
      <Field
        key={spec.key}
        label={spec.label}
        hint={spec.hint}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextInput
            type="number"
            min={0}
            value={value}
            onChange={(event) => setDraft({ ...draft, [spec.key]: event.target.value })}
            style={{ width: '110px' }}
          />
          <span style={{ fontSize: '13px', color: colors.textMuted }}>{spec.unit}</span>
          {isUnlimited ? (
            <span
              style={{
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
                textTransform: 'uppercase', color: colors.accentText,
                border: `1px solid ${colors.accent}`, borderRadius: '4px', padding: '2px 7px',
              }}
            >
              No limit
            </span>
          ) : null}
        </div>
      </Field>
    )
  }

  return (
    <div>
      <PageHeader
        title="Sending rules"
        subtitle="How often a customer can be sent marketing notifications."
        actions={(
          <>
            <PillButton variant="ghost" onClick={() => navigate('/notifications')}>
              <ArrowLeft size={14} /> Notification Center
            </PillButton>
            <PillButton onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save rules'}
            </PillButton>
          </>
        )}
      />

      {error ? (
        <Card style={{ marginBottom: '16px', borderColor: '#B45309' }}>
          <span style={{ color: '#B45309' }}>{error}</span>
        </Card>
      ) : null}
      {notice ? (
        <Card style={{ marginBottom: '16px', borderColor: '#15803D' }}>
          <span style={{ color: '#15803D' }}>{notice}</span>
        </Card>
      ) : null}

      <div className="ntf-split">
        <div style={{ display: 'grid', gap: '18px' }}>
          <section className="ntf-panel">
            <header className="ntf-panel__head">
              <div>
                <div className="ntf-eyebrow">Frequency</div>
                <h2 className="font-display ntf-panel__title">How often you can send</h2>
                <p className="ntf-panel__hint">
                  Set any of these to <strong>0</strong> to remove the limit entirely. With every
                  cap at 0 a customer can receive as many campaigns as you send.
                </p>
              </div>
            </header>
            <div className="ntf-panel__body" style={{ display: 'grid', gap: '16px' }}>
              {CAPS.map(renderField)}
            </div>
          </section>

          <section className="ntf-panel">
            <header className="ntf-panel__head">
              <div>
                <div className="ntf-eyebrow">Quiet hours</div>
                <h2 className="font-display ntf-panel__title">When not to send</h2>
                <p className="ntf-panel__hint">
                  Local time. A campaign scheduled inside the window is held until it opens rather
                  than cancelled.
                </p>
              </div>
            </header>
            <div className="ntf-panel__body" style={{ display: 'grid', gap: '16px' }}>
              {QUIET.map(renderField)}
            </div>
          </section>
        </div>

        <div className="ntf-rail">
          <Card>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <ShieldCheck size={17} color={colors.textMuted} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, color: colors.textPrimary, fontSize: '13.5px' }}>
                  What these rules never override
                </div>
                <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: colors.textMuted, lineHeight: 1.55 }}>
                  A customer who has switched a category off in the app stays off, whatever these
                  numbers say. The same is true of a campaign marked to ignore the caps. Consent is
                  theirs; frequency is yours.
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="ntf-eyebrow" style={{ marginBottom: '10px' }}>In effect now</div>
            {settings ? (
              <div>
                <div className="ntf-row">
                  <span style={{ color: colors.textSecondary }}>Per day</span>
                  <span className="ntf-row__value">
                    {settings.effective.perDay > 0 ? settings.effective.perDay : 'No limit'}
                  </span>
                </div>
                <div className="ntf-row">
                  <span style={{ color: colors.textSecondary }}>Per week</span>
                  <span className="ntf-row__value">
                    {settings.effective.perWeek > 0 ? settings.effective.perWeek : 'No limit'}
                  </span>
                </div>
                <div className="ntf-row">
                  <span style={{ color: colors.textSecondary }}>Minimum gap</span>
                  <span className="ntf-row__value">
                    {settings.effective.minGapMinutes > 0 ? `${settings.effective.minGapMinutes} min` : 'No limit'}
                  </span>
                </div>
                <div className="ntf-row">
                  <span style={{ color: colors.textSecondary }}>Quiet hours</span>
                  <span className="ntf-row__value">
                    {settings.effective.quietStart === settings.effective.quietEnd
                      ? 'Off'
                      : `${settings.effective.quietStart}:00 to ${settings.effective.quietEnd}:00`}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: colors.textMuted }}>Loading…</div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
